import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Scale, Download, Eye, User, FileText, CheckCircle, Clock, AlertCircle, ToggleLeft, ToggleRight, Trash2, FileCheck, IdCard, MessageCircleWarning, ThumbsUp, ThumbsDown, Calendar, Archive, Mail, MessageCircle, Inbox, RefreshCw, MapPin, Upload, Loader2, Bell, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import ErrorBoundary from '../../components/ErrorBoundary';

// ── Card admin para configurar ventana de convocatoria (iter177) ──
function ConvocatoriaConfigCard({ convocatoria, onGuardar, guardando }) {
  const [fechaApertura, setFechaApertura] = useState('');
  const [fechaCierre, setFechaCierre] = useState('');
  const [anioActuacion, setAnioActuacion] = useState('');

  useEffect(() => {
    if (convocatoria) {
      setFechaApertura(convocatoria.fecha_apertura || '');
      setFechaCierre(convocatoria.fecha_cierre || '');
      setAnioActuacion(convocatoria.anio_actuacion || '');
    }
  }, [convocatoria?.fecha_apertura, convocatoria?.fecha_cierre, convocatoria?.anio_actuacion]);

  if (!convocatoria) return null;

  const dirty =
    fechaApertura !== (convocatoria.fecha_apertura || '') ||
    fechaCierre !== (convocatoria.fecha_cierre || '') ||
    String(anioActuacion) !== String(convocatoria.anio_actuacion || '');

  return (
    <Card className="mb-4 border-amber-200 bg-amber-50/40" data-testid="convocatoria-config-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-amber-900">
          <Calendar className="w-4 h-4" /> Convocatoria vigente
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Año de actuación</Label>
            <Input
              type="number"
              value={anioActuacion}
              onChange={(e) => setAnioActuacion(e.target.value)}
              placeholder="Ej: 2027"
              className="h-9"
              data-testid="input-anio-actuacion-config"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Fecha de apertura</Label>
            <Input
              type="date"
              value={fechaApertura}
              onChange={(e) => setFechaApertura(e.target.value)}
              className="h-9"
              data-testid="input-fecha-apertura"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Fecha de cierre</Label>
            <Input
              type="date"
              value={fechaCierre}
              onChange={(e) => setFechaCierre(e.target.value)}
              className="h-9"
              data-testid="input-fecha-cierre"
            />
          </div>
          <Button
            size="sm"
            disabled={!dirty || guardando}
            onClick={() =>
              onGuardar({
                anio_actuacion: anioActuacion ? Number(anioActuacion) : null,
                fecha_apertura: fechaApertura || null,
                fecha_cierre: fechaCierre || null,
              })
            }
            data-testid="btn-guardar-convocatoria"
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </div>
        {convocatoria.motivo_cierre && (
          <div className="text-xs text-amber-900 bg-amber-100 px-2 py-1 rounded" data-testid="convocatoria-motivo">
            ⚠️ {convocatoria.motivo_cierre}
          </div>
        )}
        <div className="text-xs text-slate-500">
          Estado efectivo:{' '}
          <strong className={convocatoria.inscripciones_abiertas ? 'text-green-700' : 'text-red-700'}>
            {convocatoria.inscripciones_abiertas ? 'Abierta' : 'Cerrada'}
          </strong>
          {!convocatoria.inscripciones_abiertas && convocatoria.flag_manual && (
            <span className="ml-2">(cerrada por fechas · toggle manual = abierto)</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Convierte cualquier valor a string seguro para render. Nunca devuelve
// un objeto/array crudo (React se rompe: "Objects are not valid as a React child").
const safeText = (v, fallback = '-') => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'string') return v.trim() || fallback;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    const parts = v
      .filter(x => x !== null && x !== undefined)
      .map(x => (typeof x === 'object' ? JSON.stringify(x) : String(x)));
    return parts.length ? parts.join(', ') : fallback;
  }
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return fallback;
    }
  }
  return String(v);
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// iter199 · Header de columna clickeable para ordenar la tabla. Muestra un
// ícono diferente según el estado: ↕ (neutro) / ↑ (asc activo) / ↓ (desc activo).
const SortableTh = ({ field, label, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  const Icon = !active ? ArrowUpDown : (sortDir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th className="text-left py-3 px-4 font-medium text-gray-600">
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1.5 hover:text-gray-900 transition-colors ${
          active ? 'text-purple-700 font-semibold' : ''
        }`}
        data-testid={`sort-${field}`}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={`Ordenar por ${label}`}
      >
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ${active ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

export default function AdminAuxiliaresPage() {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [inscripcionesAbiertas, setInscripcionesAbiertas] = useState(false);
  const [togglingInscripciones, setTogglingInscripciones] = useState(false);
  // iter186: toggle independiente de inscripciones de otros distritos.
  const [otrosDistritosAbiertas, setOtrosDistritosAbiertas] = useState(false);
  const [togglingOtrosDistritos, setTogglingOtrosDistritos] = useState(false);
  // Filtro por tipo de inscripción (iter126)
  const [filtroTipo, setFiltroTipo] = useState('todos'); // 'todos' | 'primera_vez' | 'renovacion' | 'sin_declarar'
  // iter199 · Ordenamiento de la tabla por columna. sortField=null => orden
  // original por fecha_envio desc (el que viene del backend).
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'  // Filtro por estado (auditoría / correcciones)
  const [filtroEstado, setFiltroEstado] = useState('todos'); // 'todos' | 'corregidas' | 'anuladas'
  // Estado de exportación a Excel
  const [exportando, setExportando] = useState(false);
  // Estado del diálogo "Observar"
  const [observarDialog, setObservarDialog] = useState({ open: false, solicitud: null, motivo: '', punto_reinicio: 'inicio_formulario' });
  const [observando, setObservando] = useState(false);
  const [aprobandoId, setAprobandoId] = useState(null);
  // Estado del diálogo "Rechazar"
  const [rechazarDialog, setRechazarDialog] = useState({ open: false, solicitud: null, motivo: '' });
  const [rechazando, setRechazando] = useState(false);
  // Estado de "Reenviar notificación" (para deshabilitar el botón mientras vuela)
  const [reenviandoId, setReenviandoId] = useState(null);
  const [reenviandoWaId, setReenviandoWaId] = useState(null);
  // Filtro por año de actuación (iter177) — default: año vigente
  const [anioActuacion, setAnioActuacion] = useState(null); // number | null (todos)
  const [aniosDisponibles, setAniosDisponibles] = useState([]);
  const [anioVigente, setAnioVigente] = useState(null);
  // Estado extendido de convocatoria (fechas + motivo)
  const [convocatoria, setConvocatoria] = useState(null);
  const [guardandoConvocatoria, setGuardandoConvocatoria] = useState(false);
  // Carga manual de adjuntos por parte del admin (Upload en el modal Ver)
  const [uploadingTipo, setUploadingTipo] = useState(null); // 'certificado' | 'copia_dni' | 'formulario_firmado' | null
  const [uploadingDepto, setUploadingDepto] = useState(null); // depto en curso, sirve para spinner por fila
  const uploadInputRefs = useRef({});
  // iter180 · Selector de depto al subir un formulario firmado en solicitudes multi-depto.
  // Cuando el admin clickea "Cargar/Reemplazar" el archivo elegido queda `staged`
  // esperando confirmación del depto en el dialog. Al confirmar se dispara la subida.
  const [deptoPickerDialog, setDeptoPickerDialog] = useState({
    open: false,
    file: null,          // File pendiente
    deptos: [],          // opciones
    solicitud: null,     // solicitud target
  });
  // Preview de adjuntos: modal con <img> para imágenes o <iframe> para PDFs
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    url: null,
    mimeType: null,
    filename: null,
    tipoLabel: null,
    loading: false,
  });
  const { token } = useAuth();

  useEffect(() => {
    fetchEstadoInscripciones();
    fetchAniosDisponibles();
  }, []);

  // Cuando cambia el año seleccionado (o al montar), recargamos solicitudes
  useEffect(() => {
    fetchSolicitudes();
  }, [anioActuacion]);

  const fetchAniosDisponibles = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/auxiliares-justicia/anios-disponibles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAniosDisponibles(res.data.anios || []);
      setAnioVigente(res.data.anio_vigente);
      // Default al vigente si el usuario no eligió nada aún
      if (anioActuacion === null && res.data.anio_vigente) {
        setAnioActuacion(res.data.anio_vigente);
      }
    } catch (err) {
      console.error('Error cargando años disponibles:', err);
    }
  };

  const fetchEstadoInscripciones = async () => {
    try {
      const res = await axios.get(`${API_URL}/auxiliares-justicia/estado-inscripcion`);
      setInscripcionesAbiertas(res.data.inscripciones_abiertas);
      setConvocatoria(res.data);
      setOtrosDistritosAbiertas(Boolean(res.data.inscripciones_otros_distritos_abiertas));
    } catch (err) {
      console.error('Error obteniendo estado de inscripciones:', err);
    }
  };

  const handleToggleOtrosDistritos = async () => {
    setTogglingOtrosDistritos(true);
    try {
      const res = await axios.patch(
        `${API_URL}/admin/auxiliares-justicia/toggle-otros-distritos`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOtrosDistritosAbiertas(Boolean(res.data.habilitado));
    } catch (err) {
      console.error('Error toggling otros distritos:', err);
      alert('Error al cambiar el estado de inscripciones de otros distritos');
    } finally {
      setTogglingOtrosDistritos(false);
    }
  };

  const guardarConvocatoria = async ({ fecha_apertura, fecha_cierre, anio_actuacion }) => {
    setGuardandoConvocatoria(true);
    try {
      const res = await axios.put(
        `${API_URL}/admin/auxiliares-justicia/convocatoria`,
        { fecha_apertura, fecha_cierre, anio_actuacion },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setConvocatoria(res.data);
      setInscripcionesAbiertas(res.data.inscripciones_abiertas);
    } catch (err) {
      console.error('Error guardando convocatoria:', err);
      alert('Error al guardar la configuración de convocatoria: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGuardandoConvocatoria(false);
    }
  };

  const handleToggleInscripciones = async () => {
    setTogglingInscripciones(true);
    try {
      const res = await axios.patch(
        `${API_URL}/admin/auxiliares-justicia/toggle-inscripciones`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // El backend ahora devuelve el estado extendido completo — misma
      // forma que /estado-inscripcion y PUT /convocatoria. Actualizamos
      // ambas piezas con una única fuente de verdad.
      setInscripcionesAbiertas(res.data.inscripciones_abiertas);
      setConvocatoria(res.data);
    } catch (err) {
      console.error('Error cambiando estado de inscripciones:', err);
      alert('Error al cambiar el estado de las inscripciones');
    } finally {
      setTogglingInscripciones(false);
    }
  };

  const fetchSolicitudes = async () => {
    try {
      const params = anioActuacion ? { anio_actuacion: anioActuacion } : {};
      const res = await axios.get(`${API_URL}/admin/auxiliares-justicia/solicitudes`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setSolicitudes(res.data);
    } catch (err) {
      console.error('Error cargando solicitudes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (solicitud) => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Formulario_${solicitud.datos?.apellido}_${solicitud.datos?.nombres}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando PDF:', err);
      alert('Error al descargar el PDF. El archivo puede no estar disponible.');
    }
  };

  // Nueva función para descargar adjuntos v2
  // Nombres humanos por tipo
  const TIPO_LABELS = {
    formulario_firmado: 'Formulario_Firmado',
    certificado: 'Certificado',
    copia_dni: 'DNI',
  };

  // Devuelve la extensión (con punto) leída del filename original persistido
  // por el backend en `solicitud.adjuntos.{tipo}_nombre`. Soporta pdf/img.
  const extensionDeAdjunto = (solicitud, tipo) => {
    const nombreOriginal = solicitud?.adjuntos?.[`${tipo}_nombre`] || '';
    const m = /\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i.exec(nombreOriginal);
    return m ? m[0].toLowerCase() : '';
  };

  // MIME type inferido de la extensión para armar el Blob del preview
  const mimeDeExtension = (ext) => {
    switch ((ext || '').toLowerCase()) {
      case '.pdf': return 'application/pdf';
      case '.png': return 'image/png';
      case '.webp': return 'image/webp';
      case '.heic':
      case '.heif': return 'image/heic';
      case '.jpg':
      case '.jpeg':
      default: return 'image/jpeg';
    }
  };

  const cerrarPreview = () => {
    if (previewDialog.url) {
      try { window.URL.revokeObjectURL(previewDialog.url); } catch (_) {}
    }
    setPreviewDialog({ open: false, url: null, mimeType: null, filename: null, tipoLabel: null, loading: false });
  };

  // iter180 · resuelve el `tipo` con :idx si el caller pidió un depto
  // específico para formulario_firmado.
  const resolverTipoConDepto = (solicitud, tipo, depto) => {
    if (tipo !== 'formulario_firmado' || !depto || depto === 'unico') return tipo;
    const arr = solicitud?.adjuntos?.formularios_firmados_por_departamento;
    if (!Array.isArray(arr)) return tipo;
    const idx = arr.findIndex(f => (f?.departamento || 'unico') === depto);
    return idx >= 0 ? `formulario_firmado:${idx}` : tipo;
  };

  const handlePreviewAdjuntoV2 = async (solicitud, tipo, depto = null) => {
    const tipoResuelto = resolverTipoConDepto(solicitud, tipo, depto);
    // Abrimos el modal en loading para feedback inmediato
    setPreviewDialog({
      open: true, url: null,
      mimeType: null,
      filename: `${TIPO_LABELS[tipo] || tipo}${extensionDeAdjunto(solicitud, tipo)}`,
      tipoLabel: TIPO_LABELS[tipo] || tipo,
      loading: true,
    });
    try {
      const ext = extensionDeAdjunto(solicitud, tipo);
      const mime = mimeDeExtension(ext);
      const response = await axios.get(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/adjunto-v2/${tipoResuelto}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      // Preferimos el content-type real del backend; si no hay, usamos el inferido
      const serverMime = response.data?.type || response.headers?.['content-type'] || mime;
      const blob = new Blob([response.data], { type: serverMime });
      const url = window.URL.createObjectURL(blob);
      const apellido = (solicitud.datos?.apellido || 'archivo').replace(/\s+/g, '_');
      const suffix = depto && depto !== 'unico' ? `_${depto.replace(/\s+/g, '_')}` : '';
      setPreviewDialog({
        open: true,
        url,
        mimeType: serverMime,
        filename: `${TIPO_LABELS[tipo] || tipo}_${apellido}${suffix}${ext}`,
        tipoLabel: TIPO_LABELS[tipo] || tipo,
        loading: false,
      });
    } catch (err) {
      console.error(`Error previsualizando ${tipo}:`, err);
      cerrarPreview();
      toast.error('No se pudo cargar la vista previa del archivo.');
    }
  };

  const handleDownloadAdjuntoV2 = async (solicitud, tipo, depto = null) => {
    const tipoResuelto = resolverTipoConDepto(solicitud, tipo, depto);
    try {
      const response = await axios.get(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/adjunto-v2/${tipoResuelto}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      // Extensión real del archivo original (guardado en solicitud.adjuntos.{tipo}_nombre).
      // Antes se forzaba ".pdf" siempre → rompía .jpg / .png. Ahora la respetamos igual
      // que en la descarga por ZIP.
      const extension = extensionDeAdjunto(solicitud, tipo);
      const apellido = (solicitud.datos?.apellido || 'archivo').replace(/\s+/g, '_');
      const suffix = depto && depto !== 'unico' ? `_${depto.replace(/\s+/g, '_')}` : '';
      link.setAttribute('download', `${TIPO_LABELS[tipo] || tipo}_${apellido}${suffix}${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Error descargando ${tipo}:`, err);
      alert(`Error al descargar el archivo. Puede no estar disponible.`);
    }
  };


  // iter180 · devuelve los departamentos judiciales de una solicitud (multi
  // o único). Fallback al modelo legacy `departamento_judicial` (string).
  const getDeptosSolicitud = (sol) => {
    if (!sol) return [];
    const arr = sol?.datos?.departamentos_judiciales;
    if (Array.isArray(arr) && arr.length) return arr;
    const legacy = sol?.datos?.departamento_judicial;
    if (typeof legacy === 'string' && legacy.trim()) return [legacy.trim()];
    return [];
  };

  // Devuelve el registro del formulario firmado para un depto específico
  // (o null si aún no se cargó ese firmado). En el modelo legacy sin depto
  // se guarda con `departamento="unico"`.
  const getFirmadoPorDepto = (sol, depto) => {
    if (!sol) return null;
    const arr = sol?.adjuntos?.formularios_firmados_por_departamento;
    if (Array.isArray(arr) && arr.length) {
      const match = arr.find(f => (f?.departamento || 'unico') === (depto || 'unico'));
      return match || null;
    }
    // Legacy: un solo firmado sin depto → lo asociamos al primer depto único
    const legacyKey = sol?.adjuntos?.formulario_firmado;
    if (legacyKey && (!depto || depto === 'unico')) {
      return { departamento: 'unico', key: legacyKey, filename: sol?.adjuntos?.formulario_firmado_nombre };
    }
    return null;
  };

  // Handler para el botón Upload de formulario firmado (multi-depto aware).
  // Si la solicitud tiene 2+ deptos y el admin no eligió aún → abre picker.
  // Si la solicitud tiene 1 depto o legacy → sube directo.
  const handleUploadFormularioFirmado = (solicitud, file, deptoOverride = null) => {
    if (!file) return;
    const deptos = getDeptosSolicitud(solicitud);
    if (deptoOverride) {
      // Ya elegido por el picker
      return handleAdminUploadAdjunto(solicitud, 'formulario_firmado', file, deptoOverride);
    }
    if (deptos.length <= 1) {
      // Único / legacy: mando "unico" para asegurar consistencia con el array
      return handleAdminUploadAdjunto(solicitud, 'formulario_firmado', file, deptos[0] || 'unico');
    }
    // Multi-depto → abrimos picker
    setDeptoPickerDialog({ open: true, file, deptos, solicitud });
  };

  // Carga manual de adjunto por parte del admin (Certificado / DNI / Formulario firmado)
  const handleAdminUploadAdjunto = async (solicitud, tipo, file, departamento = null) => {
    if (!file) return;
    const tipoLabel = {
      certificado: 'Certificado',
      copia_dni: 'DNI',
      formulario_firmado: 'Formulario firmado',
    }[tipo] || tipo;
    setUploadingTipo(tipo);
    setUploadingDepto(departamento);
    try {
      const fd = new FormData();
      fd.append('tipo', tipo);
      fd.append('archivo', file);
      if (departamento) fd.append('departamento', departamento);
      const res = await axios.post(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/adjuntar-documento`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000,
        }
      );
      // Actualizar la solicitud seleccionada en el modal
      const nuevaSolicitud = {
        ...solicitud,
        adjuntos: res.data.adjuntos || solicitud.adjuntos,
      };
      setSelectedSolicitud(nuevaSolicitud);
      // Sincronizar con la lista global
      setSolicitudes(prev => prev.map(s => (s.id === solicitud.id ? nuevaSolicitud : s)));
      toast.success(`${tipoLabel} cargado con éxito`, {
        description: `Archivo: ${file.name}`,
        duration: 4000,
      });
    } catch (err) {
      console.error(`Error subiendo ${tipo}:`, err);
      const detalle = err?.response?.data?.detail || `No se pudo cargar el ${tipoLabel}. Reintentá.`;
      toast.error('Error al cargar el archivo', { description: detalle });
    } finally {
      setUploadingTipo(null);
      setUploadingDepto(null);
      // Limpiar el input para permitir re-seleccionar el mismo archivo
      const ref = uploadInputRefs.current[tipo];
      if (ref) ref.value = '';
    }
  };


  // Legacy: descargar adjuntos viejos (1, 2)
  const handleDownloadAdjuntoLegacy = async (solicitud, num) => {
    try {
      const nombreAdjunto = num === 1 ? solicitud.adjunto1_nombre : solicitud.adjunto2_nombre;
      const response = await axios.get(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/adjunto/${num}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreAdjunto || `adjunto${num}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Error descargando adjunto ${num}:`, err);
      alert(`Error al descargar el adjunto. El archivo puede no estar disponible.`);
    }
  };

  // Descargar ZIP con todos los documentos (timeout explícito de 90s)
  const handleDownloadZip = async (solicitud) => {
    const ZIP_DOWNLOAD_TIMEOUT_MS = 90000; // 90 segundos
    const loadingToastId = toast.loading('Descargando ZIP...', {
      description: 'Esto puede tardar unos segundos si el archivo aún no fue generado.',
    });
    try {
      const datos = solicitud.datos || {};
      const zipFilename = solicitud.adjuntos?.zip_filename ||
        `${datos.apellido?.replace(/\s/g, '')}${datos.nombres?.replace(/\s/g, '')}${datos.matricula || ''}.zip`;

      const response = await axios.get(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/zip`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: ZIP_DOWNLOAD_TIMEOUT_MS,
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', zipFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP descargado correctamente', { id: loadingToastId });
    } catch (err) {
      console.error('Error descargando ZIP:', err);
      const isTimeout = err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '');
      if (isTimeout) {
        toast.error('El servidor tardó demasiado, reintentá en un momento', {
          id: loadingToastId,
          description: 'La generación del ZIP superó los 90 segundos. Si el problema persiste, contactá al equipo técnico.',
        });
        return;
      }
      // Intentar leer el mensaje de error desde un Blob (responseType: 'blob')
      let errorMessage = 'Error al descargar el ZIP. Es posible que esta solicitud fue enviada con una versión anterior del sistema y los archivos no están disponibles.';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) errorMessage = parsed.detail;
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
      } catch (_) {
        // Mantener mensaje por defecto si el blob no es JSON
      }
      toast.error('No se pudo descargar el ZIP', {
        id: loadingToastId,
        description: errorMessage,
      });
    }
  };

  const handleObservarAbrir = (solicitud) => {
    setObservarDialog({
      open: true,
      solicitud,
      motivo: solicitud?.motivo_observacion || '',
      punto_reinicio: solicitud?.punto_reinicio_observacion || 'inicio_formulario',
    });
  };

  const handleObservarConfirmar = async () => {
    const { solicitud, motivo, punto_reinicio } = observarDialog;
    if (!solicitud) return;
    if (!motivo.trim()) {
      toast.error('Escribí un motivo antes de observar la solicitud.');
      return;
    }
    setObservando(true);
    try {
      const r = await axios.patch(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/observar`,
        { motivo: motivo.trim(), punto_reinicio: punto_reinicio || 'inicio_formulario' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSolicitudes(prev => prev.map(s => (
        s.id === solicitud.id
          ? {
              ...s,
              estado: 'observada',
              motivo_observacion: motivo.trim(),
              punto_reinicio_observacion: punto_reinicio || 'inicio_formulario',
              fecha_observacion: new Date().toISOString(),
            }
          : s
      )));
      setObservarDialog({ open: false, solicitud: null, motivo: '', punto_reinicio: 'inicio_formulario' });
      if (r.data?.email_enviado) {
        toast.success('Solicitud observada · notificación enviada al matriculado');
      } else {
        toast.warning('Solicitud observada, pero no se pudo enviar la notificación por email');
      }
    } catch (err) {
      console.error('Error observando solicitud:', err);
      toast.error(err.response?.data?.detail || 'Error al observar la solicitud');
    } finally {
      setObservando(false);
    }
  };

  const handleAprobar = async (solicitud) => {
    if (!window.confirm(`¿Aprobar la solicitud de ${solicitud.datos?.apellido}, ${solicitud.datos?.nombres}?`)) return;
    setAprobandoId(solicitud.id);
    try {
      const r = await axios.patch(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/aprobar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSolicitudes(prev => prev.map(s => (
        s.id === solicitud.id ? { ...s, estado: 'aprobada' } : s
      )));
      if (r.data?.email_enviado) {
        toast.success('Solicitud aprobada · notificación enviada al matriculado');
      } else {
        toast.warning('Solicitud aprobada, pero no se pudo enviar la notificación por email');
      }
    } catch (err) {
      console.error('Error aprobando:', err);
      toast.error(err.response?.data?.detail || 'Error al aprobar la solicitud');
    } finally {
      setAprobandoId(null);
    }
  };

  const handleRechazarAbrir = (solicitud) => {
    setRechazarDialog({ open: true, solicitud, motivo: solicitud?.motivo_rechazo || '' });
  };

  const handleRechazarConfirmar = async () => {
    const { solicitud, motivo } = rechazarDialog;
    if (!solicitud) return;
    if (!motivo.trim()) {
      toast.error('Escribí un motivo antes de rechazar la solicitud.');
      return;
    }
    setRechazando(true);
    try {
      const r = await axios.patch(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/rechazar`,
        { motivo: motivo.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSolicitudes(prev => prev.map(s => (
        s.id === solicitud.id
          ? { ...s, estado: 'rechazada', motivo_rechazo: motivo.trim(), fecha_rechazo: new Date().toISOString() }
          : s
      )));
      setRechazarDialog({ open: false, solicitud: null, motivo: '' });
      if (r.data?.email_enviado) {
        toast.success('Solicitud rechazada · notificación enviada al matriculado');
      } else {
        toast.warning('Solicitud rechazada, pero no se pudo enviar la notificación por email');
      }
    } catch (err) {
      console.error('Error rechazando solicitud:', err);
      toast.error(err.response?.data?.detail || 'Error al rechazar la solicitud');
    } finally {
      setRechazando(false);
    }
  };

  const handleReenviarNotificacion = async (solicitud) => {
    // El tipo se deriva del estado actual, para el backend.
    const map = { aprobada: 'aprobacion', observada: 'observacion', rechazada: 'rechazo' };
    const tipo = map[solicitud.estado];
    if (!tipo) return;
    setReenviandoId(solicitud.id);
    try {
      await axios.post(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/reenviar-notificacion`,
        { tipo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notificación enviada al matriculado');
    } catch (err) {
      console.error('Error reenviando notificación:', err);
      toast.error(err.response?.data?.detail || 'No se pudo reenviar la notificación');
    } finally {
      setReenviandoId(null);
    }
  };

  const handleReenviarWhatsApp = async (solicitud) => {
    if (!solicitud?.id) return;
    setReenviandoWaId(solicitud.id);
    try {
      const res = await axios.post(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}/reenviar-whatsapp`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // El backend ahora devuelve `detalle` legible con envíos OK/fallidos.
      const detalle = res?.data?.detalle;
      const intentos = res?.data?.intentos || [];
      const okCount = intentos.filter(i => i.enviado).length;
      const failCount = intentos.filter(i => !i.enviado).length;
      if (failCount > 0 && okCount > 0) {
        toast.warning(`WhatsApp enviado parcialmente. ${detalle || ''}`);
      } else if (okCount > 1) {
        toast.success(`WhatsApp enviado a ${okCount} números.`);
      } else {
        toast.success('WhatsApp enviado al matriculado.');
      }
    } catch (err) {
      console.error('Error reenviando WhatsApp:', err);
      const detail = err.response?.data?.detail || 'No se pudo reenviar el WhatsApp';
      toast.error(detail);
    } finally {
      setReenviandoWaId(null);
    }
  };

  const handleDeleteSolicitud = async (solicitud) => {
    if (!window.confirm(`¿Estás seguro de eliminar la solicitud de ${solicitud.datos?.nombres} ${solicitud.datos?.apellido}?`)) {
      return;
    }
    
    try {
      await axios.delete(
        `${API_URL}/admin/auxiliares-justicia/solicitudes/${solicitud.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSolicitudes(prev => prev.filter(s => s.id !== solicitud.id));
      if (showModal && selectedSolicitud?.id === solicitud.id) {
        setShowModal(false);
        setSelectedSolicitud(null);
      }
    } catch (err) {
      console.error('Error eliminando solicitud:', err);
      alert('Error al eliminar la solicitud');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTipoBadge = (tipo) => {
    if (tipo === 'renovacion') {
      return <Badge className="bg-purple-100 text-purple-800 border border-purple-200" data-testid="badge-tipo-renovacion">Renovación</Badge>;
    }
    if (tipo === 'primera_vez') {
      return <Badge className="bg-sky-100 text-sky-800 border border-sky-200" data-testid="badge-tipo-primera-vez">Primera vez</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-500 border border-gray-200">Sin declarar</Badge>;
  };

  const getTipoInscripcion = (sol) => sol?.tipo_inscripcion || sol?.datos?.tipo_inscripcion || '';

  const solicitudesFiltradas = solicitudes.filter((s) => {
    // Filtro por tipo
    if (filtroTipo !== 'todos') {
      const t = getTipoInscripcion(s);
      if (filtroTipo === 'sin_declarar') {
        if (t) return false;
      } else if (t !== filtroTipo) {
        return false;
      }
    }
    // Filtro por estado
    if (filtroEstado === 'corregidas' && s.estado !== 'pendiente_nueva_revision') return false;
    if (filtroEstado === 'anuladas' && s.estado !== 'anulada_por_usuario') return false;
    if (filtroEstado === 'pendientes' && !(s.estado === 'enviada' || s.estado === 'enviado')) return false;
    if (filtroEstado === 'observadas' && s.estado !== 'observada') return false;
    if (filtroEstado === 'aprobadas' && s.estado !== 'aprobada') return false;
    if (filtroEstado === 'rechazadas' && s.estado !== 'rechazada') return false;
    return true;
  });

  // iter199 · Ordenamiento por columna. Cada clave extrae un valor normalizado
  // (lower-case para strings, parseInt para DNIs, Date.getTime para fechas).
  // Mantener este map en sync con los <SortableTh> del render.
  const SORT_EXTRACTORS = {
    solicitante: (s) =>
      `${(s.datos?.apellido || '').toLowerCase()} ${(s.datos?.nombres || '').toLowerCase()}`.trim(),
    documento: (s) => {
      const raw = (s.datos?.documento || '').toString().replace(/[^0-9]/g, '');
      return raw ? parseInt(raw, 10) : Number.MAX_SAFE_INTEGER;
    },
    tipo: (s) => (getTipoInscripcion(s) || 'zzz').toLowerCase(), // sin declarar al final
    distrito: (s) =>
      s.es_distrito_lomas === false
        ? `otro ${(s.distrito_origen || '').toLowerCase()}`
        : 'lomas de zamora',
    fueros: (s) => {
      const c = s.datos?.fuero_civil ? 1 : 0;
      const l = s.datos?.fuero_laboral ? 2 : 0;
      return c + l; // 0 / 1 / 2 / 3
    },
    fecha: (s) => {
      const t = s.fecha_envio ? new Date(s.fecha_envio).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    },
    estado: (s) => (s.estado || '').toLowerCase(),
  };

  const solicitudesOrdenadas = useMemo(() => {
    if (!sortField || !SORT_EXTRACTORS[sortField]) return solicitudesFiltradas;
    const extractor = SORT_EXTRACTORS[sortField];
    const arr = [...solicitudesFiltradas];
    arr.sort((a, b) => {
      const va = extractor(a);
      const vb = extractor(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudesFiltradas, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Mismo campo → toggle dir, y si ya está desc, resetea al orden original.
      if (sortDir === 'asc') setSortDir('desc');
      else {
        setSortField(null);
        setSortDir('asc');
      }
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'enviado':
      case 'enviada':
        return <Badge className="bg-blue-100 text-blue-700" data-testid="badge-estado-enviada">Enviada</Badge>;
      case 'pendiente_nueva_revision':
        return (
          <Badge
            className="bg-purple-100 text-purple-800 border border-purple-300 ring-1 ring-purple-200 animate-pulse"
            data-testid="badge-estado-pendiente-nueva-revision"
          >
            Corregida · nueva revisión
          </Badge>
        );
      case 'observada':
        return <Badge className="bg-amber-100 text-amber-800" data-testid="badge-estado-observada">Observada</Badge>;
      case 'aprobada':
        return <Badge className="bg-green-100 text-green-700" data-testid="badge-estado-aprobada">Aprobada</Badge>;
      case 'rechazada':
        return <Badge className="bg-red-100 text-red-700" data-testid="badge-estado-rechazada">Rechazada</Badge>;
      case 'anulada_por_usuario':
        return <Badge className="bg-gray-100 text-gray-600 border border-gray-300" data-testid="badge-estado-anulada">Anulada por usuario</Badge>;
      case 'error_envio':
      case 'error_envio_colegio':
        return <Badge className="bg-red-100 text-red-700">Error</Badge>;
      case 'pendiente':
        return <Badge className="bg-yellow-100 text-yellow-700">Pendiente</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{estado}</Badge>;
    }
  };

  // Detectar si es estructura nueva o vieja
  const isNuevaEstructura = (solicitud) => {
    return solicitud.adjuntos && Object.keys(solicitud.adjuntos).length > 0;
  };

  /**
   * Devuelve `true` sólo si el adjunto pedido tiene una key válida en la base
   * (no vacía, no espacios en blanco, no null). Se usa para decidir si
   * renderizar los iconos de descarga en la lista y el modal, evitando 404
   * innecesarios sobre archivos que nunca se cargaron.
   *
   * Estructura NUEVA: `solicitud.adjuntos[tipo]` es un string (key en Object Storage).
   * Estructura LEGACY: se accede a `solicitud.adjuntoN_path`.
   */
  const hasAdjunto = (solicitud, tipo) => {
    if (!solicitud) return false;
    if (isNuevaEstructura(solicitud)) {
      // Para formulario_firmado también aceptamos el array por-departamento.
      if (tipo === 'formulario_firmado') {
        const arr = solicitud.adjuntos?.formularios_firmados_por_departamento;
        if (Array.isArray(arr) && arr.some(x => x && typeof x.key === 'string' && x.key.trim())) {
          return true;
        }
      }
      const key = solicitud.adjuntos?.[tipo];
      return typeof key === 'string' && key.trim().length > 0;
    }
    // Legacy
    if (tipo === 'adjunto1') {
      return typeof solicitud.adjunto1_path === 'string' && solicitud.adjunto1_path.trim().length > 0;
    }
    if (tipo === 'adjunto2') {
      return typeof solicitud.adjunto2_path === 'string' && solicitud.adjunto2_path.trim().length > 0;
    }
    return false;
  };

  const handleVerDetalle = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-center" richColors />
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Scale className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auxiliares de Justicia</h1>
            <p className="text-gray-500 text-sm">Solicitudes de inscripción al listado oficial</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.assign('/admin/auxiliares-justicia/recordatorios')}
            className="border-amber-300 text-amber-800 hover:bg-amber-50 flex-1 sm:flex-initial"
            data-testid="btn-recordatorios-anuales"
          >
            <Bell className="w-4 h-4 mr-1" />
            Recordatorios Anuales
          </Button>
          {/* Selector de Año de Actuación (iter177) */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2" data-testid="selector-anio-container">
            <span className="text-sm font-medium text-gray-700">Año:</span>
            <Select
              value={String(anioActuacion || '')}
              onValueChange={(v) => setAnioActuacion(v === 'todos' ? null : Number(v))}
            >
              <SelectTrigger className="h-8 w-[110px]" data-testid="select-anio-actuacion">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {aniosDisponibles.map((a) => (
                  <SelectItem key={a} value={String(a)}>
                    {a} {a === anioVigente ? '· vigente' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Toggle de inscripciones — refleja el estado EFECTIVO de la
              convocatoria (flag_manual + ventana de fechas). Al clickear
              alterna solo el `flag_manual`; si tus fechas ya cerraron,
              esto no vuelve a abrir hasta corregir la fecha_cierre. */}
          <div className="flex items-center gap-3 bg-white border rounded-lg px-4 py-2">
            <span className="text-sm font-medium text-gray-700">Inscripciones:</span>
            <button
              onClick={handleToggleInscripciones}
              disabled={togglingInscripciones}
              title={
                convocatoria?.motivo_cierre ||
                (inscripcionesAbiertas
                  ? 'Click para forzar cierre'
                  : (convocatoria?.flag_manual
                      ? 'Cerradas por fechas (ver Convocatoria vigente)'
                      : 'Click para habilitar (respeta ventana de fechas)'))
              }
              className={`flex items-center gap-2 px-3 py-1 rounded-full transition-colors ${
                inscripcionesAbiertas
                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
              data-testid="toggle-inscripciones-btn"
            >
              {togglingInscripciones ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              ) : inscripcionesAbiertas ? (
                <ToggleRight className="w-5 h-5" />
              ) : (
                <ToggleLeft className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">
                {inscripcionesAbiertas ? 'Abiertas' : 'Cerradas'}
              </span>
              {convocatoria && !inscripcionesAbiertas && convocatoria.flag_manual && (
                <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-white/60 text-gray-600" data-testid="badge-cerrada-por-fechas">
                  por fechas
                </span>
              )}
            </button>
          </div>
          {/* iter186: Toggle independiente para inscripciones de matriculados
              de otros distritos a los Deptos Judiciales Lomas / Avellaneda-Lanús. */}
          <div className="flex items-center gap-3 bg-white border rounded-lg px-4 py-2">
            <span className="text-sm font-medium text-gray-700">Otros distritos:</span>
            <button
              onClick={handleToggleOtrosDistritos}
              disabled={togglingOtrosDistritos}
              title={
                otrosDistritosAbiertas
                  ? 'Click para cerrar inscripciones de otros distritos'
                  : 'Click para habilitar inscripciones de otros distritos (independiente del toggle general)'
              }
              className={`flex items-center gap-2 px-3 py-1 rounded-full transition-colors ${
                otrosDistritosAbiertas
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="toggle-otros-distritos-btn"
            >
              {togglingOtrosDistritos ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              ) : otrosDistritosAbiertas ? (
                <ToggleRight className="w-5 h-5" />
              ) : (
                <ToggleLeft className="w-5 h-5" />
              )}
              <span className="text-sm font-medium">
                {otrosDistritosAbiertas ? 'Habilitado' : 'Deshabilitado'}
              </span>
            </button>
          </div>
          <Badge variant="outline" className="text-base sm:text-lg px-3 sm:px-4 py-1.5 sm:py-2">
            {solicitudes.length} solicitudes
          </Badge>
        </div>
      </div>

      {/* Convocatoria: ventana temporal + año (iter177) */}
      <ConvocatoriaConfigCard
        convocatoria={convocatoria}
        onGuardar={guardarConvocatoria}
        guardando={guardandoConvocatoria}
      />

      {/* Stats — refleja el flujo real: recibidas → revisión → observadas/aprobadas/rechazadas */}
      {(() => {
        const totalRecibidas = solicitudes.length;
        const pendientesRevision = solicitudes.filter(s => s.estado === 'enviada' || s.estado === 'enviado').length;
        const corregidas = solicitudes.filter(s => s.estado === 'pendiente_nueva_revision').length;
        const observadas = solicitudes.filter(s => s.estado === 'observada').length;
        const aprobadas = solicitudes.filter(s => s.estado === 'aprobada').length;
        const rechazadas = solicitudes.filter(s => s.estado === 'rechazada').length;

        // Distribución por Departamento Judicial (una solicitud puede tener 1 o 2)
        const getDeptos = (s) => {
          if (Array.isArray(s.departamentos_judiciales)) return s.departamentos_judiciales;
          if (Array.isArray(s.datos?.departamentos_judiciales)) return s.datos.departamentos_judiciales;
          return [];
        };
        const countLomas = solicitudes.filter(s => getDeptos(s).includes('Lomas de Zamora')).length;
        const countAvellaneda = solicitudes.filter(s => getDeptos(s).includes('Avellaneda-Lanús')).length;

        const statCards = [
          { key: 'total', label: 'Total recibidas', value: totalRecibidas, Icon: Inbox, iconBg: 'bg-slate-100', iconColor: 'text-slate-700', ring: 'ring-slate-200', activeRing: 'ring-slate-500', activeBg: 'bg-slate-50' },
          { key: 'pendientes', label: 'Pendientes de revisión', value: pendientesRevision, Icon: Clock, iconBg: 'bg-blue-100', iconColor: 'text-blue-600', ring: 'ring-blue-200', activeRing: 'ring-blue-500', activeBg: 'bg-blue-50' },
          { key: 'corregidas', label: 'Corregidas / Re-enviadas', value: corregidas, Icon: RefreshCw, iconBg: 'bg-violet-100', iconColor: 'text-violet-600', ring: 'ring-violet-200', activeRing: 'ring-violet-500', activeBg: 'bg-violet-50' },
          { key: 'observadas', label: 'Observadas', value: observadas, Icon: MessageCircleWarning, iconBg: 'bg-amber-100', iconColor: 'text-amber-600', ring: 'ring-amber-200', activeRing: 'ring-amber-500', activeBg: 'bg-amber-50' },
          { key: 'aprobadas', label: 'Aprobadas', value: aprobadas, Icon: CheckCircle, iconBg: 'bg-green-100', iconColor: 'text-green-600', ring: 'ring-green-200', activeRing: 'ring-green-500', activeBg: 'bg-green-50' },
          { key: 'rechazadas', label: 'Rechazadas', value: rechazadas, Icon: ThumbsDown, iconBg: 'bg-red-100', iconColor: 'text-red-600', ring: 'ring-red-200', activeRing: 'ring-red-500', activeBg: 'bg-red-50' },
        ];

        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {statCards.map(({ key, label, value, Icon, iconBg, iconColor, ring, activeRing, activeBg }) => {
                // Map card key → valor de filtroEstado
                const filterKey = key === 'total' ? 'todos' : key;
                const isActive = filtroEstado === filterKey;
                const handleClick = () => {
                  // Toggle: si ya está activa, se resetea a 'todos'
                  const next = isActive ? 'todos' : filterKey;
                  setFiltroEstado(next);
                  // Scroll suave hacia el listado
                  requestAnimationFrame(() => {
                    const el = document.querySelector('[data-testid="listado-solicitudes-card"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  });
                };
                return (
                  <Card
                    key={key}
                    onClick={handleClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClick();
                      }
                    }}
                    aria-pressed={isActive}
                    className={`cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${
                      isActive ? `ring-2 ${activeRing} ${activeBg} shadow-md` : `ring-1 ${ring}`
                    }`}
                    data-testid={`stat-card-${key}`}
                    data-active={isActive ? 'true' : 'false'}
                  >
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 ${iconBg} rounded-full flex-shrink-0`}>
                          <Icon className={`w-5 h-5 ${iconColor}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-2xl font-bold leading-tight" data-testid={`stat-${key}-value`}>{value}</p>
                          <p className="text-xs text-gray-500 leading-snug mt-0.5">{label}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Distribución por Departamento Judicial */}
            <Card className="ring-1 ring-purple-100" data-testid="stat-card-deptos">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-purple-600" />
                  <p className="text-sm font-semibold text-gray-700">Distribución por Departamento Judicial</p>
                  <span className="text-xs text-gray-400 ml-auto">Una solicitud puede elegir uno o ambos</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-purple-50 border border-purple-100">
                    <div>
                      <p className="text-xs text-purple-700 font-medium">Lomas de Zamora</p>
                      <p className="text-2xl font-bold text-purple-900" data-testid="stat-depto-lomas-value">{countLomas}</p>
                    </div>
                    <MapPin className="w-8 h-8 text-purple-300" />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-100">
                    <div>
                      <p className="text-xs text-indigo-700 font-medium">Avellaneda-Lanús</p>
                      <p className="text-2xl font-bold text-indigo-900" data-testid="stat-depto-avellaneda-value">{countAvellaneda}</p>
                    </div>
                    <MapPin className="w-8 h-8 text-indigo-300" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Lista de solicitudes */}
      <Card data-testid="listado-solicitudes-card">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Listado de Solicitudes</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                disabled={exportando || solicitudesFiltradas.length === 0}
                data-testid="btn-exportar-excel"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  setExportando(true);
                  try {
                    const params = new URLSearchParams();
                    if (filtroTipo !== 'todos') params.set('tipo', filtroTipo);
                    if (filtroEstado !== 'todos') params.set('estado', filtroEstado);
                    const res = await axios.get(
                      `${API_URL}/admin/auxiliares-justicia/exportar-excel?${params.toString()}`,
                      { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                    );
                    // Descargar el blob
                    const blob = new Blob([res.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    a.download = `auxiliares_justicia_${stamp}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error(e);
                    alert('No se pudo exportar el Excel. Revisá la consola para más detalles.');
                  } finally {
                    setExportando(false);
                  }
                }}
              >
                {exportando ? (
                  <><Clock className="w-4 h-4 mr-2 animate-spin" /> Generando…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Exportar Datos ({solicitudesFiltradas.length})</>
                )}
              </Button>
              <div className="flex items-center gap-2 flex-wrap" data-testid="filtros-tipo-inscripcion">
              {[
                { k: 'todos', label: 'Todas', count: solicitudes.length },
                { k: 'primera_vez', label: 'Primera vez', count: solicitudes.filter(s => getTipoInscripcion(s) === 'primera_vez').length },
                { k: 'renovacion', label: 'Renovación', count: solicitudes.filter(s => getTipoInscripcion(s) === 'renovacion').length },
                { k: 'sin_declarar', label: 'Sin declarar', count: solicitudes.filter(s => !getTipoInscripcion(s)).length },
              ].map(f => (
                <button
                  key={f.k}
                  type="button"
                  onClick={() => setFiltroTipo(f.k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    filtroTipo === f.k
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`filtro-tipo-${f.k}`}
                >
                  {f.label} <span className="ml-1 opacity-70">({f.count})</span>
                </button>
              ))}
            </div>
            </div>
          </div>
          {/* Filtros por estado (auditoría) */}
          <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="filtros-estado">
            <span className="text-xs text-gray-500 mr-1">Estado:</span>
            {[
              { k: 'todos', label: 'Todos', count: solicitudes.length },
              {
                k: 'corregidas',
                label: 'Corregidas',
                count: solicitudes.filter(s => s.estado === 'pendiente_nueva_revision').length,
                highlight: 'purple',
              },
              {
                k: 'anuladas',
                label: 'Anuladas',
                count: solicitudes.filter(s => s.estado === 'anulada_por_usuario').length,
              },
            ].map(f => {
              const isActive = filtroEstado === f.k;
              const purple = f.highlight === 'purple';
              return (
                <button
                  key={f.k}
                  type="button"
                  onClick={() => setFiltroEstado(f.k)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    isActive
                      ? (purple
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-gray-800 text-white border-gray-800')
                      : (purple && f.count > 0
                          ? 'bg-purple-50 text-purple-800 border-purple-200 hover:bg-purple-100'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50')
                  }`}
                  data-testid={`filtro-estado-${f.k}`}
                >
                  {f.label} <span className="ml-1 opacity-70">({f.count})</span>
                </button>
              );
            })}
          </div>
        </CardHeader>
        <CardContent>
          {solicitudesFiltradas.length === 0 ? (
            <div className="text-center py-12">
              <Scale className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {solicitudes.length === 0
                  ? 'No hay solicitudes registradas'
                  : 'No hay solicitudes que coincidan con el filtro'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <SortableTh field="solicitante" label="Solicitante" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="documento" label="DNI" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="tipo" label="Tipo" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="distrito" label="Distrito" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="fueros" label="Fueros" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="fecha" label="Fecha" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <SortableTh field="estado" label="Estado" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    <th className="text-left py-3 px-4 font-medium text-gray-600">Adjuntos</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitudesOrdenadas.map((solicitud) => (
                    <tr
                      key={solicitud.id}
                      className={`border-b hover:bg-gray-50 ${
                        solicitud.estado === 'pendiente_nueva_revision'
                          ? 'bg-purple-50/60 border-l-4 border-l-purple-500'
                          : ''
                      }`}
                      data-testid={`fila-solicitud-${solicitud.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <User className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="font-medium">
                            {solicitud.datos?.apellido}, {solicitud.datos?.nombres}
                          </span>
                          {solicitud.es_otro_distrito && (
                            <Badge
                              className="text-[10px] bg-amber-500 text-white hover:bg-amber-600 border-0"
                              data-testid={`chip-otro-distrito-${solicitud.id}`}
                              title={`Inscripción pública desde ${solicitud.distrito_origen || 'otro distrito'}`}
                            >
                              {solicitud.distrito_origen || 'Otro distrito'}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {solicitud.datos?.documento || '-'}
                      </td>
                      <td className="py-3 px-4">
                        {getTipoBadge(getTipoInscripcion(solicitud))}
                      </td>
                      <td className="py-3 px-4">
                        {solicitud.es_distrito_lomas === false ? (
                          <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">Otro distrito</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Lomas de Zamora</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          {solicitud.datos?.fuero_civil && (
                            <Badge variant="outline" className="text-xs">Civil</Badge>
                          )}
                          {solicitud.datos?.fuero_laboral && (
                            <Badge variant="outline" className="text-xs">Laboral</Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {formatDate(solicitud.fecha_envio)}
                      </td>
                      <td className="py-3 px-4">
                        {getEstadoBadge(solicitud.estado)}
                        {solicitud.sin_certificado_aun && (
                          <div className="mt-1">
                            <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700">
                              Sin certificado aún
                            </Badge>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {/* PDF generado */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadPdf(solicitud)}
                            className="h-7 px-2"
                            title="Formulario PDF generado"
                          >
                            <FileText className="w-4 h-4 text-amber-600" />
                          </Button>
                          
                          {/* Nueva estructura */}
                          {isNuevaEstructura(solicitud) ? (
                            <>
                              {/* iter182 · una fila de Ver/Descargar por cada
                                  departamento con formulario firmado */}
                              {(() => {
                                const deptos = getDeptosSolicitud(solicitud);
                                const opciones = deptos.length ? deptos : ['unico'];
                                return opciones.map((dep) => {
                                  const firmado = getFirmadoPorDepto(solicitud, dep);
                                  if (!firmado) return null;
                                  const paramDep = dep === 'unico' ? null : dep;
                                  const shortDep = dep === 'unico'
                                    ? ''
                                    : (dep.match(/[A-ZÁÉÍÓÚÑ]/g) || dep.slice(0, 2).split('')).slice(0, 3).join('').toUpperCase();
                                  return (
                                    <React.Fragment key={`firmado-${solicitud.id}-${dep}`}>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handlePreviewAdjuntoV2(solicitud, 'formulario_firmado', paramDep)}
                                        className="h-7 px-2"
                                        title={`Vista previa Formulario firmado${paramDep ? ` — ${dep}` : ''}`}
                                        data-testid={`btn-preview-formulario-${solicitud.id}-${(dep || 'unico').replace(/\s+/g, '-')}`}
                                      >
                                        <Eye className="w-4 h-4 text-green-700" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDownloadAdjuntoV2(solicitud, 'formulario_firmado', paramDep)}
                                        className="h-7 px-2 gap-1"
                                        title={`Descargar Formulario firmado${paramDep ? ` — ${dep}` : ''}`}
                                        data-testid={`btn-desc-formulario-${solicitud.id}-${(dep || 'unico').replace(/\s+/g, '-')}`}
                                      >
                                        <FileCheck className="w-4 h-4 text-green-600" />
                                        {shortDep && (
                                          <span className="text-[10px] font-semibold text-green-700 leading-none">{shortDep}</span>
                                        )}
                                      </Button>
                                    </React.Fragment>
                                  );
                                });
                              })()}
                              {hasAdjunto(solicitud, 'certificado') && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePreviewAdjuntoV2(solicitud, 'certificado')}
                                    className="h-7 px-2"
                                    title="Vista previa Certificado/Acta"
                                    data-testid={`btn-preview-certificado-${solicitud.id}`}
                                  >
                                    <Eye className="w-4 h-4 text-blue-700" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadAdjuntoV2(solicitud, 'certificado')}
                                    className="h-7 px-2"
                                    title="Descargar Certificado/Acta"
                                    data-testid={`btn-desc-certificado-${solicitud.id}`}
                                  >
                                    <FileText className="w-4 h-4 text-blue-600" />
                                  </Button>
                                </>
                              )}
                              {hasAdjunto(solicitud, 'copia_dni') && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePreviewAdjuntoV2(solicitud, 'copia_dni')}
                                    className="h-7 px-2"
                                    title="Vista previa DNI"
                                    data-testid={`btn-preview-dni-${solicitud.id}`}
                                  >
                                    <Eye className="w-4 h-4 text-purple-700" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadAdjuntoV2(solicitud, 'copia_dni')}
                                    className="h-7 px-2"
                                    title="Descargar Copia DNI"
                                    data-testid={`btn-desc-dni-${solicitud.id}`}
                                  >
                                    <IdCard className="w-4 h-4 text-purple-600" />
                                  </Button>
                                </>
                              )}
                            </>
                          ) : (
                            /* Estructura legacy */
                            <>
                              {hasAdjunto(solicitud, 'adjunto1') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAdjuntoLegacy(solicitud, 1)}
                                  className="h-7 px-2"
                                  title={solicitud.adjunto1_nombre}
                                >
                                  <Download className="w-4 h-4 text-blue-600" />
                                </Button>
                              )}
                              {hasAdjunto(solicitud, 'adjunto2') && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAdjuntoLegacy(solicitud, 2)}
                                  className="h-7 px-2"
                                  title={solicitud.adjunto2_nombre}
                                >
                                  <Download className="w-4 h-4 text-green-600" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVerDetalle(solicitud)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver
                          </Button>
                          {solicitud.estado !== 'aprobada' && solicitud.estado !== 'rechazada' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleObservarAbrir(solicitud)}
                              className="border-amber-300 text-amber-800 hover:bg-amber-50"
                              title="Marcar como observada y notificar al matriculado"
                              data-testid={`btn-observar-${solicitud.id}`}
                            >
                              <MessageCircleWarning className="w-4 h-4 mr-1" />
                              Observar
                            </Button>
                          )}
                          {solicitud.estado !== 'aprobada' && solicitud.estado !== 'rechazada' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAprobar(solicitud)}
                              disabled={aprobandoId === solicitud.id}
                              className="border-green-300 text-green-800 hover:bg-green-50"
                              title="Aprobar la solicitud (envía notificación por email)"
                              data-testid={`btn-aprobar-${solicitud.id}`}
                            >
                              <ThumbsUp className="w-4 h-4 mr-1" />
                              {aprobandoId === solicitud.id ? '...' : 'Aprobar'}
                            </Button>
                          )}
                          {solicitud.estado !== 'aprobada' && solicitud.estado !== 'rechazada' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRechazarAbrir(solicitud)}
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              title="Rechazar la solicitud (envía notificación por email)"
                              data-testid={`btn-rechazar-${solicitud.id}`}
                            >
                              <ThumbsDown className="w-4 h-4 mr-1" />
                              Rechazar
                            </Button>
                          )}

                          {/* Reenvíos de notificación (según estado) — solo icono */}
                          {solicitud.estado === 'aprobada' && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleReenviarNotificacion(solicitud)}
                              disabled={reenviandoId === solicitud.id}
                              className="border-green-300 text-green-700 hover:bg-green-50"
                              title="Reenviar mail de aprobación al matriculado"
                              data-testid={`btn-reenviar-aprobacion-${solicitud.id}`}
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                          {solicitud.estado === 'observada' && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleReenviarNotificacion(solicitud)}
                              disabled={reenviandoId === solicitud.id}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              title="Reenviar mail de observación al matriculado"
                              data-testid={`btn-reenviar-observacion-${solicitud.id}`}
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}
                          {solicitud.estado === 'rechazada' && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleReenviarNotificacion(solicitud)}
                              disabled={reenviandoId === solicitud.id}
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              title="Reenviar mail de rechazo al matriculado"
                              data-testid={`btn-reenviar-rechazo-${solicitud.id}`}
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Reenvío de WhatsApp: sólo si hay resolución + al menos un celular
                              (padrón o formulario). Tooltip dinámico muestra a qué números irá. */}
                          {['aprobada', 'observada', 'rechazada'].includes(solicitud.estado) && (solicitud.has_celular_padron || solicitud.has_celular_formulario) && (
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleReenviarWhatsApp(solicitud)}
                              disabled={reenviandoWaId === solicitud.id}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              title={(() => {
                                const partes = [];
                                if (solicitud.has_celular_padron && solicitud.celular_padron_display) {
                                  partes.push(`${solicitud.celular_padron_display} (padrón)`);
                                }
                                if (solicitud.has_celular_formulario && solicitud.celular_formulario_display && !solicitud.celulares_coinciden) {
                                  partes.push(`${solicitud.celular_formulario_display} (formulario)`);
                                }
                                if (solicitud.celulares_coinciden) {
                                  return `Reenviar WhatsApp a ${solicitud.celular_padron_display} (padrón y formulario coinciden)`;
                                }
                                return `Reenviar WhatsApp a: ${partes.join(' y ')}`;
                              })()}
                              data-testid={`btn-reenviar-whatsapp-${solicitud.id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {['aprobada', 'observada', 'rechazada'].includes(solicitud.estado) && !solicitud.has_celular_padron && !solicitud.has_celular_formulario && (
                            <Button
                              variant="outline"
                              size="icon"
                              disabled
                              className="border-gray-200 text-gray-300 cursor-not-allowed opacity-50"
                              title="El matriculado no tiene celular cargado (ni en padrón ni en formulario)"
                              data-testid={`btn-reenviar-whatsapp-disabled-${solicitud.id}`}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSolicitud(solicitud)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Eliminar solicitud"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de detalle */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-600" />
              Detalle de Solicitud
            </DialogTitle>
          </DialogHeader>
          
          {selectedSolicitud && (
            <ErrorBoundary
              label="el detalle de esta solicitud"
              onReset={() => setShowModal(false)}
            >
            <div className="space-y-6">
              {/* Estado y observación (si aplica) */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-600">Estado:</span>
                {getEstadoBadge(selectedSolicitud.estado)}
                {selectedSolicitud.fecha_reenvio && (
                  <span className="text-xs text-gray-500">
                    · Reenviada el {formatDate(selectedSolicitud.fecha_reenvio)}
                  </span>
                )}
              </div>

              {selectedSolicitud.estado === 'observada' && selectedSolicitud.motivo_observacion && (
                <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4" data-testid="detail-motivo-observacion">
                  <div className="flex items-start gap-2">
                    <MessageCircleWarning className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-amber-900">Motivo de la observación</p>
                      <p className="text-sm text-amber-900 whitespace-pre-wrap mt-1">
                        {safeText(selectedSolicitud.motivo_observacion)}
                      </p>
                      {selectedSolicitud.admin_observador_email && (
                        <p className="text-xs text-amber-700 mt-2">
                          Marcada por {selectedSolicitud.admin_observador_email}
                          {selectedSolicitud.fecha_observacion && ` · ${formatDate(selectedSolicitud.fecha_observacion)}`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(selectedSolicitud.historial_observaciones || []).length > 1 && (
                <details className="text-sm text-gray-600">
                  <summary className="cursor-pointer font-medium">Historial de observaciones ({(selectedSolicitud.historial_observaciones || []).length})</summary>
                  <ul className="mt-2 space-y-2 pl-4">
                    {(selectedSolicitud.historial_observaciones || []).map((h, idx) => (
                      <li key={idx} className="border-l-2 border-amber-200 pl-3">
                        <p className="text-xs text-gray-500">{formatDate(h.fecha)} · {h.admin_email || '—'}</p>
                        <p className="whitespace-pre-wrap">{h.motivo}</p>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Historial de Cambios (auditoría completa) */}
              {(selectedSolicitud.historial_actividad || []).length > 0 && (
                <div
                  className="bg-white border border-gray-200 rounded-lg p-4"
                  data-testid="historial-cambios"
                >
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    Historial de Cambios
                    <span className="text-xs font-normal text-gray-500">
                      ({(selectedSolicitud.historial_actividad || []).length})
                    </span>
                  </h3>
                  <ol className="relative border-l-2 border-gray-200 pl-4 space-y-3">
                    {[...(selectedSolicitud.historial_actividad || [])]
                      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
                      .map((h, idx) => {
                        const meta = ({
                          creacion:              { label: 'Solicitud creada',              dot: 'bg-blue-500',   text: 'text-blue-700'   },
                          reenvio_correccion:    { label: 'Reenvío tras corrección',       dot: 'bg-purple-500', text: 'text-purple-700' },
                          observacion:           { label: 'Solicitud observada',           dot: 'bg-amber-500',  text: 'text-amber-700'  },
                          aprobacion:            { label: 'Solicitud aprobada',            dot: 'bg-green-500',  text: 'text-green-700'  },
                          reenvio_bloqueado:     { label: 'Intento de reenvío bloqueado',  dot: 'bg-red-500',    text: 'text-red-700'    },
                          anulacion_por_usuario: { label: 'Envío anulado por el usuario',  dot: 'bg-gray-500',   text: 'text-gray-700'   },
                        })[h.tipo] || { label: h.tipo || 'Cambio', dot: 'bg-gray-400', text: 'text-gray-700' };
                        return (
                          <li key={h.id || idx} className="ml-0">
                            <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${meta.dot}`}></span>
                            <div className="text-sm">
                              <p className={`font-medium ${meta.text}`}>{meta.label}</p>
                              <p className="text-xs text-gray-500">
                                {formatDate(h.fecha)}
                                {h.actor_email ? ` · ${h.actor_email}` : ''}
                                {h.estado_anterior && h.estado_nuevo && h.estado_anterior !== h.estado_nuevo ? (
                                  <> · <span className="font-mono">{h.estado_anterior} → {h.estado_nuevo}</span></>
                                ) : null}
                              </p>
                              {h.detalle ? (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{safeText(h.detalle)}</p>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                  </ol>
                </div>
              )}

              {/* Información del caso */}
              {(selectedSolicitud.es_distrito_lomas !== undefined || selectedSolicitud.curso_este_anio !== undefined || getTipoInscripcion(selectedSolicitud)) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h3 className="font-semibold text-amber-900 mb-2">Información del Caso</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2 flex items-center gap-2">
                      <span className="text-gray-600">Tipo de inscripción:</span>
                      {getTipoBadge(getTipoInscripcion(selectedSolicitud))}
                    </div>
                    <div>
                      <span className="text-gray-600">Distrito:</span>
                      <p className="font-medium">
                        {selectedSolicitud.es_distrito_lomas === false ? 'Otro distrito' : 'Lomas de Zamora'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-600">Curso:</span>
                      <p className="font-medium">
                        {selectedSolicitud.curso_este_anio ? 'Este año' : 'Años anteriores'}
                      </p>
                    </div>
                    {selectedSolicitud.sin_certificado_aun && (
                      <div className="col-span-2">
                        <Badge className="bg-yellow-100 text-yellow-700">
                          ⚠️ Pendiente de certificado (curso de este año)
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Info del solicitante */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Datos Personales</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Nombre completo:</span>
                    <p className="font-medium">
                      {safeText(selectedSolicitud.datos?.apellido)}, {safeText(selectedSolicitud.datos?.nombres)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">DNI:</span>
                    <p className="font-medium">{safeText(selectedSolicitud.datos?.documento)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{safeText(selectedSolicitud.datos?.email_personal)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Nacionalidad:</span>
                    <p className="font-medium">{safeText(selectedSolicitud.datos?.nacionalidad)}</p>
                  </div>
                </div>
              </div>

              {/* Domicilios */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Domicilios</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="text-gray-500">Profesional:</span>
                    <p>
                      {safeText(selectedSolicitud.datos?.domicilio_profesional)}
                      {safeText(selectedSolicitud.datos?.localidad_profesional, '') && ` - ${safeText(selectedSolicitud.datos?.localidad_profesional)}`}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Real:</span>
                    <p>{safeText(selectedSolicitud.datos?.domicilio_real)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Legal:</span>
                    {(() => {
                      // Nuevo modelo: domicilios_legales es un objeto {depto: {domicilio, cp, localidad, tel}}.
                      // Legacy: domicilio_legal + localidad_legal planos.
                      const dlMap = selectedSolicitud.datos?.domicilios_legales;
                      const entries = dlMap && typeof dlMap === 'object' && !Array.isArray(dlMap)
                        ? Object.entries(dlMap)
                        : [];
                      if (entries.length > 0) {
                        return (
                          <ul className="mt-1 space-y-1.5" data-testid="detail-domicilios-legales">
                            {entries.map(([depto, dl]) => {
                              const info = dl && typeof dl === 'object' ? dl : {};
                              const dir = safeText(info.domicilio, '');
                              const loc = safeText(info.localidad, '');
                              const cp = safeText(info.cp, '');
                              const tel = safeText(info.tel, '');
                              const linea = [dir, loc && `- ${loc}`, cp && `(CP ${cp})`, tel && `· Tel: ${tel}`]
                                .filter(Boolean).join(' ');
                              return (
                                <li key={depto} className="border-l-2 border-amber-300 pl-2">
                                  <strong className="text-amber-800">{safeText(depto)}:</strong>{' '}
                                  {linea || '-'}
                                </li>
                              );
                            })}
                          </ul>
                        );
                      }
                      // Fallback legacy
                      const legDir = safeText(selectedSolicitud.datos?.domicilio_legal);
                      const legLoc = safeText(selectedSolicitud.datos?.localidad_legal, '');
                      return (
                        <p>{legDir}{legLoc && ` - ${legLoc}`}</p>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Formación */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Formación</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Título:</span>
                    <p className="font-medium">{safeText(selectedSolicitud.datos?.titulo_habilitante)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Fecha graduación:</span>
                    <p className="font-medium">{safeText(selectedSolicitud.datos?.fecha_graduacion)}</p>
                  </div>
                </div>
              </div>

              {/* Inscripción */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Datos de Inscripción</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Fuero Civil:</span>
                      {selectedSolicitud.datos?.fuero_civil ? 
                        <Badge className="bg-green-100 text-green-700">Sí</Badge> : 
                        <Badge className="bg-gray-100 text-gray-500">No</Badge>
                      }
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Fuero Laboral:</span>
                      {selectedSolicitud.datos?.fuero_laboral ? 
                        <Badge className="bg-green-100 text-green-700">Sí</Badge> : 
                        <Badge className="bg-gray-100 text-gray-500">No</Badge>
                      }
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Domicilio electrónico Corte:</span>
                    <p>{safeText(selectedSolicitud.datos?.domicilio_electronico_corte)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Curso procesal:</span>
                    <p>{safeText(selectedSolicitud.datos?.lugar_fecha_curso_procesal)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Departamentos Judiciales:</span>
                    {(() => {
                      const deptosArr = Array.isArray(selectedSolicitud.departamentos_judiciales)
                        ? selectedSolicitud.departamentos_judiciales
                        : (Array.isArray(selectedSolicitud.datos?.departamentos_judiciales)
                            ? selectedSolicitud.datos.departamentos_judiciales
                            : []);
                      if (deptosArr.length > 0) {
                        return (
                          <p data-testid="detail-departamentos-list">
                            {deptosArr.map(d => safeText(d)).join(', ')}
                          </p>
                        );
                      }
                      // Fallback al campo legacy sedes_inscripcion.
                      return <p>{safeText(selectedSolicitud.datos?.sedes_inscripcion)}</p>;
                    })()}
                  </div>
                </div>
              </div>

              {/* Archivos adjuntos */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Archivos Adjuntos</h3>
                <div className="space-y-2">
                  {/* PDF Generado */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-amber-600" />
                      <span className="text-sm">Formulario PDF (generado)</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPdf(selectedSolicitud)}>
                      <Download className="w-4 h-4 mr-1" /> Descargar
                    </Button>
                  </div>
                  
                  {/* Nueva estructura */}
                  {isNuevaEstructura(selectedSolicitud) ? (
                    <>
                      {/* Inputs ocultos para carga manual del admin */}
                      <input
                        ref={el => { if (el) uploadInputRefs.current.formulario_firmado = el; }}
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          const dep = e.target.dataset.deptoTarget || null;
                          // Limpiamos la marca para no arrastrarla en clicks futuros
                          delete e.target.dataset.deptoTarget;
                          handleUploadFormularioFirmado(selectedSolicitud, f, dep);
                        }}
                        data-testid="admin-upload-input-formulario"
                      />
                      <input
                        ref={el => { if (el) uploadInputRefs.current.certificado = el; }}
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => handleAdminUploadAdjunto(selectedSolicitud, 'certificado', e.target.files?.[0])}
                        data-testid="admin-upload-input-certificado"
                      />
                      <input
                        ref={el => { if (el) uploadInputRefs.current.copia_dni = el; }}
                        type="file"
                        accept="application/pdf,image/jpeg,image/png,image/jpg,image/webp"
                        className="hidden"
                        onChange={(e) => handleAdminUploadAdjunto(selectedSolicitud, 'copia_dni', e.target.files?.[0])}
                        data-testid="admin-upload-input-dni"
                      />

                      {/* iter180 · Formulario firmado — una fila por
                          cada departamento. Descarga individual, upload
                          por depto (con selector si es multi). */}
                      {(() => {
                        const deptos = getDeptosSolicitud(selectedSolicitud);
                        const opciones = deptos.length ? deptos : ['unico'];
                        return opciones.map((dep) => {
                          const firmado = getFirmadoPorDepto(selectedSolicitud, dep);
                          const label = dep === 'unico'
                            ? 'Formulario firmado'
                            : `Formulario firmado — ${dep}`;
                          const rowTestId = `modal-firmado-row-${dep.replace(/\s+/g, '-')}`;
                          const isUploadingThis = uploadingTipo === 'formulario_firmado'
                            && (uploadingDepto === dep || (opciones.length === 1 && !uploadingDepto));
                          if (firmado) {
                            return (
                              <div key={dep} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-green-50 rounded-lg" data-testid={rowTestId}>
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <FileCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                                  <div className="flex flex-col min-w-0">
                                    <span className="text-sm font-medium truncate">{label}</span>
                                    {firmado.filename && (
                                      <span className="text-xs text-gray-500 truncate">{firmado.filename}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                                  <Button
                                    variant="outline" size="sm"
                                    onClick={() => handlePreviewAdjuntoV2(selectedSolicitud, 'formulario_firmado', dep === 'unico' ? null : dep)}
                                    title="Vista previa"
                                    className="h-8 px-2"
                                    data-testid={`btn-preview-firmado-${dep.replace(/\s+/g, '-')}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span className="hidden md:inline ml-1">Ver</span>
                                  </Button>
                                  <Button
                                    variant="outline" size="sm"
                                    onClick={() => handleDownloadAdjuntoV2(selectedSolicitud, 'formulario_firmado', dep === 'unico' ? null : dep)}
                                    title="Descargar"
                                    className="h-8 px-2"
                                    data-testid={`btn-descargar-firmado-${dep.replace(/\s+/g, '-')}`}
                                  >
                                    <Download className="w-4 h-4" />
                                    <span className="hidden md:inline ml-1">Descargar</span>
                                  </Button>
                                  <Button
                                    variant="outline" size="sm"
                                    onClick={() => {
                                      const input = uploadInputRefs.current.formulario_firmado;
                                      if (input) {
                                        input.dataset.deptoTarget = dep;
                                        input.click();
                                      }
                                    }}
                                    disabled={isUploadingThis}
                                    title={`Reemplazar formulario firmado de ${dep}`}
                                    className="h-8 px-2"
                                    data-testid={`btn-reemplazar-firmado-${dep.replace(/\s+/g, '-')}`}
                                  >
                                    {isUploadingThis ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                  </Button>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={dep} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded-lg" data-testid={rowTestId}>
                              <div className="flex items-center gap-2 text-gray-500 min-w-0 flex-1">
                                <FileCheck className="w-5 h-5 flex-shrink-0" />
                                <span className="text-sm italic truncate">{label} — no adjuntado</span>
                              </div>
                              <Button
                                variant="outline" size="sm"
                                onClick={() => {
                                  const input = uploadInputRefs.current.formulario_firmado;
                                  if (input) {
                                    input.dataset.deptoTarget = dep;
                                    input.click();
                                  }
                                }}
                                disabled={isUploadingThis}
                                title={`Cargar formulario firmado de ${dep}`}
                                className="h-8 self-end sm:self-auto flex-shrink-0"
                                data-testid={`btn-cargar-firmado-${dep.replace(/\s+/g, '-')}`}
                              >
                                {isUploadingThis
                                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo…</>
                                  : <><Upload className="w-4 h-4 mr-1" /> Cargar</>}
                              </Button>
                            </div>
                          );
                        });
                      })()}
                      {hasAdjunto(selectedSolicitud, 'certificado') ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-blue-50 rounded-lg">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <span className="text-sm truncate">Certificado / Acta del curso</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                            <Button variant="outline" size="sm" onClick={() => handlePreviewAdjuntoV2(selectedSolicitud, 'certificado')} className="h-8 px-2" data-testid="modal-preview-certificado" title="Vista previa">
                              <Eye className="w-4 h-4" />
                              <span className="hidden md:inline ml-1">Ver</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDownloadAdjuntoV2(selectedSolicitud, 'certificado')} className="h-8 px-2" title="Descargar" data-testid="modal-desc-certificado">
                              <Download className="w-4 h-4" />
                              <span className="hidden md:inline ml-1">Descargar</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => uploadInputRefs.current.certificado?.click()}
                              disabled={uploadingTipo === 'certificado'}
                              title="Reemplazar certificado"
                              className="h-8 px-2"
                              data-testid="modal-upload-certificado"
                            >
                              {uploadingTipo === 'certificado' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded-lg" data-testid="modal-cert-ausente">
                          <div className="flex items-center gap-2 text-gray-500 min-w-0 flex-1">
                            <FileText className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm italic truncate">Certificado / Acta del curso — no adjuntado</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => uploadInputRefs.current.certificado?.click()}
                            disabled={uploadingTipo === 'certificado'}
                            title="Cargar certificado en nombre del socio"
                            className="h-8 self-end sm:self-auto flex-shrink-0"
                            data-testid="modal-upload-certificado-ausente"
                          >
                            {uploadingTipo === 'certificado'
                              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo…</>
                              : <><Upload className="w-4 h-4 mr-1" /> Cargar</>}
                          </Button>
                        </div>
                      )}
                      {hasAdjunto(selectedSolicitud, 'copia_dni') ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-purple-50 rounded-lg">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <IdCard className="w-5 h-5 text-purple-600 flex-shrink-0" />
                            <span className="text-sm truncate">Copia de DNI</span>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
                            <Button variant="outline" size="sm" onClick={() => handlePreviewAdjuntoV2(selectedSolicitud, 'copia_dni')} className="h-8 px-2" data-testid="modal-preview-dni" title="Vista previa">
                              <Eye className="w-4 h-4" />
                              <span className="hidden md:inline ml-1">Ver</span>
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDownloadAdjuntoV2(selectedSolicitud, 'copia_dni')} className="h-8 px-2" title="Descargar" data-testid="modal-desc-dni">
                              <Download className="w-4 h-4" />
                              <span className="hidden md:inline ml-1">Descargar</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => uploadInputRefs.current.copia_dni?.click()}
                              disabled={uploadingTipo === 'copia_dni'}
                              title="Reemplazar copia de DNI"
                              className="h-8 px-2"
                              data-testid="modal-upload-dni"
                            >
                              {uploadingTipo === 'copia_dni' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-gray-50 rounded-lg" data-testid="modal-dni-ausente">
                          <div className="flex items-center gap-2 text-gray-500 min-w-0 flex-1">
                            <IdCard className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm italic truncate">Copia de DNI — no adjuntada</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => uploadInputRefs.current.copia_dni?.click()}
                            disabled={uploadingTipo === 'copia_dni'}
                            title="Cargar copia de DNI en nombre del socio"
                            className="h-8 self-end sm:self-auto flex-shrink-0"
                            data-testid="modal-upload-dni-ausente"
                          >
                            {uploadingTipo === 'copia_dni'
                              ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Subiendo…</>
                              : <><Upload className="w-4 h-4 mr-1" /> Cargar</>}
                          </Button>
                        </div>
                      )}
                      {selectedSolicitud.sin_certificado_aun && !hasAdjunto(selectedSolicitud, 'certificado') && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                          ⚠️ El solicitante indicó que realizó el curso este año y aún no tiene el certificado.
                        </div>
                      )}
                      
                      {/* Botón para descargar ZIP con todos los documentos */}
                      <div className="mt-4 pt-4 border-t">
                        <Button 
                          className="w-full bg-violet-600 hover:bg-violet-700"
                          onClick={() => handleDownloadZip(selectedSolicitud)}
                        >
                          <Archive className="w-4 h-4 mr-2" /> 
                          Descargar todos los documentos (ZIP)
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Estructura legacy */
                    <>
                      {hasAdjunto(selectedSolicitud, 'adjunto1') && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <span className="text-sm">{selectedSolicitud.adjunto1_nombre}</span>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadAdjuntoLegacy(selectedSolicitud, 1)}>
                            <Download className="w-4 h-4 mr-1" /> Descargar
                          </Button>
                        </div>
                      )}
                      {hasAdjunto(selectedSolicitud, 'adjunto2') && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-green-600" />
                            <span className="text-sm">{selectedSolicitud.adjunto2_nombre}</span>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => handleDownloadAdjuntoLegacy(selectedSolicitud, 2)}>
                            <Download className="w-4 h-4 mr-1" /> Descargar
                          </Button>
                        </div>
                      )}
                      
                      {/* Botón ZIP también para legacy */}
                      <div className="mt-4 pt-4 border-t">
                        <Button 
                          className="w-full bg-violet-600 hover:bg-violet-700"
                          onClick={() => handleDownloadZip(selectedSolicitud)}
                        >
                          <Archive className="w-4 h-4 mr-2" /> 
                          Descargar todos los documentos (ZIP)
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Estado y fecha */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  Enviado: {formatDate(selectedSolicitud.fecha_envio)}
                </div>
                {getEstadoBadge(selectedSolicitud.estado)}
              </div>
            </div>
            </ErrorBoundary>
          )}
        </DialogContent>
      </Dialog>

      {/* iter180 · Selector de departamento al subir formulario firmado */}
      <Dialog open={deptoPickerDialog.open} onOpenChange={(o) => { if (!o) setDeptoPickerDialog({ open: false, file: null, deptos: [], solicitud: null }); }}>
        <DialogContent className="max-w-md" data-testid="dialog-depto-picker">
          <DialogHeader>
            <DialogTitle>¿A qué departamento corresponde?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-2">
            Esta solicitud tiene formularios de <strong>{deptoPickerDialog.deptos.length} departamentos</strong>.
            Elegí a cuál corresponde el archivo que estás subiendo — se va a reemplazar sólo ese.
          </p>
          {deptoPickerDialog.file && (
            <div className="text-xs text-slate-500 truncate mb-3">
              Archivo: <strong>{deptoPickerDialog.file.name}</strong>
            </div>
          )}
          <div className="flex flex-col gap-2">
            {deptoPickerDialog.deptos.map((dep) => (
              <Button
                key={dep}
                variant="outline"
                className="justify-start"
                onClick={() => {
                  const { file, solicitud } = deptoPickerDialog;
                  setDeptoPickerDialog({ open: false, file: null, deptos: [], solicitud: null });
                  handleAdminUploadAdjunto(solicitud, 'formulario_firmado', file, dep);
                }}
                data-testid={`btn-picker-depto-${dep.replace(/\s+/g, '-')}`}
              >
                <FileCheck className="w-4 h-4 mr-2" /> {dep}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Vista previa de adjunto (imágenes o PDF inline) */}
      <Dialog open={previewDialog.open} onOpenChange={(o) => { if (!o) cerrarPreview(); }}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden" data-testid="dialog-preview-adjunto">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="flex items-center gap-2 text-slate-800">
              <Eye className="w-5 h-5" />
              Vista previa · {previewDialog.tipoLabel || 'Adjunto'}
              {previewDialog.filename && (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {previewDialog.filename}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="w-full bg-slate-100" style={{ minHeight: 480, maxHeight: '75vh' }}>
            {previewDialog.loading && (
              <div className="flex items-center justify-center h-[480px] text-slate-500 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Cargando vista previa…
              </div>
            )}
            {!previewDialog.loading && previewDialog.url && previewDialog.mimeType?.startsWith('image/') && (
              <div className="flex items-center justify-center p-4">
                <img
                  src={previewDialog.url}
                  alt={previewDialog.filename || 'preview'}
                  className="max-w-full max-h-[70vh] rounded shadow-sm border border-slate-200"
                  data-testid="preview-img"
                />
              </div>
            )}
            {!previewDialog.loading && previewDialog.url && previewDialog.mimeType?.includes('pdf') && (
              <iframe
                title={previewDialog.filename || 'preview'}
                src={previewDialog.url}
                className="w-full"
                style={{ height: '70vh', border: 0 }}
                data-testid="preview-pdf"
              />
            )}
            {!previewDialog.loading && previewDialog.url &&
              !previewDialog.mimeType?.startsWith('image/') &&
              !previewDialog.mimeType?.includes('pdf') && (
              <div className="flex flex-col items-center justify-center h-[480px] text-slate-500 gap-2 px-8 text-center">
                <FileText className="w-8 h-8" />
                <p className="text-sm">
                  Este formato no se puede previsualizar en el navegador.
                  Descargalo para revisarlo.
                </p>
              </div>
            )}
          </div>
          <DialogFooter className="px-4 py-3 border-t bg-white">
            <Button
              variant="outline"
              onClick={() => {
                // Descarga usando el mismo blob que ya cargamos: evita 2 requests
                if (previewDialog.url && previewDialog.filename) {
                  const link = document.createElement('a');
                  link.href = previewDialog.url;
                  link.setAttribute('download', previewDialog.filename);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                }
              }}
              disabled={!previewDialog.url}
              data-testid="preview-btn-descargar"
            >
              <Download className="w-4 h-4 mr-1" /> Descargar
            </Button>
            <Button onClick={cerrarPreview} data-testid="preview-btn-cerrar">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Observar solicitud */}
      <Dialog open={observarDialog.open} onOpenChange={(o) => { if (!o) setObservarDialog({ open: false, solicitud: null, motivo: '', punto_reinicio: 'inicio_formulario' }); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-observar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <MessageCircleWarning className="w-5 h-5" />
              Observar solicitud
            </DialogTitle>
          </DialogHeader>
          {observarDialog.solicitud && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Marcá esta solicitud como observada y explicá qué debe corregir el matriculado.
                Recibirá un email con este mensaje y podrá reenviar sin volver a subir los adjuntos que estén correctos.
              </p>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p><strong>Solicitante:</strong> {observarDialog.solicitud.datos?.apellido}, {observarDialog.solicitud.datos?.nombres}</p>
                <p><strong>DNI:</strong> {observarDialog.solicitud.datos?.documento}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo de la observación *</label>
                <textarea
                  className="w-full min-h-[120px] p-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                  value={observarDialog.motivo}
                  onChange={(e) => setObservarDialog(prev => ({ ...prev, motivo: e.target.value.slice(0, 1200) }))}
                  placeholder="Ej: El domicilio legal declarado para Avellaneda-Lanús está fuera del radio departamental. Corregí con una dirección dentro del partido."
                  maxLength={1200}
                  data-testid="input-motivo-observacion"
                  autoFocus
                />
                <div className={`mt-1 text-xs text-right ${
                  observarDialog.motivo.length >= 1200 ? 'text-rose-600 font-semibold' :
                  observarDialog.motivo.length >= 1000 ? 'text-amber-600' : 'text-gray-500'
                }`} data-testid="counter-motivo-observacion">
                  {observarDialog.motivo.length} / 1200
                </div>
              </div>

              {/* iter140: selector de punto de reinicio */}
              <div>
                <label className="block text-sm font-medium mb-1">Punto de reinicio</label>
                <select
                  className="w-full p-2 border rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={observarDialog.punto_reinicio}
                  onChange={(e) => setObservarDialog(prev => ({ ...prev, punto_reinicio: e.target.value }))}
                  data-testid="select-punto-reinicio"
                >
                  <option value="inicio_formulario">Inicio del Formulario</option>
                  <option value="carga_adjuntos">Carga de Adjuntos</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {observarDialog.punto_reinicio === 'carga_adjuntos'
                    ? 'El matriculado sólo re-sube los archivos. NO se re-envía el mail de PDFs para firmar (el PDF original sigue vigente).'
                    : 'El matriculado rehace el formulario desde el principio y firma un nuevo PDF.'}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setObservarDialog({ open: false, solicitud: null, motivo: '', punto_reinicio: 'inicio_formulario' })}
              disabled={observando}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleObservarConfirmar}
              disabled={observando || !observarDialog.motivo.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-confirmar-observar"
            >
              {observando ? 'Enviando…' : 'Marcar como observada'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Dialog: Rechazar solicitud */}
      <Dialog open={rechazarDialog.open} onOpenChange={(o) => { if (!o) setRechazarDialog({ open: false, solicitud: null, motivo: '' }); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-rechazar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ThumbsDown className="w-5 h-5" />
              Rechazar solicitud
            </DialogTitle>
          </DialogHeader>
          {rechazarDialog.solicitud && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Rechazá la solicitud e indicá el motivo. El matriculado recibirá un email con la
                notificación y el motivo. El rechazo es un estado terminal — a diferencia de la
                observación, no genera un nuevo borrador editable.
              </p>
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p><strong>Solicitante:</strong> {rechazarDialog.solicitud.datos?.apellido}, {rechazarDialog.solicitud.datos?.nombres}</p>
                <p><strong>DNI:</strong> {rechazarDialog.solicitud.datos?.documento}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo del rechazo *</label>
                <textarea
                  className="w-full min-h-[120px] p-3 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-y"
                  value={rechazarDialog.motivo}
                  onChange={(e) => setRechazarDialog(prev => ({ ...prev, motivo: e.target.value.slice(0, 1200) }))}
                  placeholder="Ej: Documentación no compatible con los requisitos establecidos por la SCBA para el período vigente."
                  maxLength={1200}
                  data-testid="input-motivo-rechazo"
                  autoFocus
                />
                <div className={`mt-1 text-xs text-right ${
                  rechazarDialog.motivo.length >= 1200 ? 'text-rose-600 font-semibold' :
                  rechazarDialog.motivo.length >= 1000 ? 'text-amber-600' : 'text-gray-500'
                }`} data-testid="counter-motivo-rechazo">
                  {rechazarDialog.motivo.length} / 1200
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRechazarDialog({ open: false, solicitud: null, motivo: '' })}
              disabled={rechazando}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRechazarConfirmar}
              disabled={rechazando || !rechazarDialog.motivo.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirmar-rechazar"
            >
              {rechazando ? 'Enviando…' : 'Rechazar y notificar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
